"use client";

import { CommentApi } from "@/config/cms/api";
import { useCommentList } from "@/helpers/hooks/useComments";
import BottomArrow from "@/helpers/svg/bottomArrow.svg";
import CommentPrimaryUserIcon from "@/helpers/svg/commentPrimaryUserIcon.svg";
import CommentSecondaryUserIcon from "@/helpers/svg/commentSecondaryUserIcon.svg";
import SendMessageLeft from "@/helpers/svg/sendMessageLeft.svg";
import ThumbsDown from "@/helpers/svg/thumbsDown.svg";
import ThumbsUp from "@/helpers/svg/thumbsUp.svg";
import { useMediaQuery } from "@mui/material";
import { Button, Chip, Skeleton, Textarea } from "@nextui-org/react";
import Cookies from "js-cookie";
import Link from "next/link";
import { enqueueSnackbar } from "notistack";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { ROUTES } from "@/routes/appRoutes";
import { maskMobileNumber } from "@/helpers/data.utils";
import axiosInstance from "@/config/axiosInstance";
import { getCMSApiRoute } from "@/helpers/api.util";
import classNames from "classnames";

const Comments = ({ contentKey }) => {
  const { profile } = useSelector((state) => state.Auth);
  const { items, hasMore, isLoading, onLoadMore, reloadComments } =
    useCommentList({
      fetchDelay: 500,
      contentKey,
    });
  const [body, setBody] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");

  const desktop = useMediaQuery("(min-width:1024px)");

  const handleLike = async ({ action, target }) => {
    try {
      await axiosInstance.post(
        getCMSApiRoute(action === 1 ? "comment/like" : "comment/dislike"),
        {
          commentKey: target,
        },
        {
          validateStatus: false,
        }
      );
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message || "لطفا دوباره تلاش کنید",
        {
          variant: "error",
        }
      );
    }
  };

  const CommentItem = ({ item }) => {
    const [state, setState] = useState(item?.userAction?.value);

    return (
      <div
        key={item._id + item.userAction?.updatedAt}
        className="detail-comment-card"
      >
        <div className="detail-comment-header">
          <div className="detail-comment-author-wrap">
            {desktop && (
              <div className="detail-comment-avatar detail-comment-avatar-small">
                <CommentSecondaryUserIcon />
              </div>
            )}
            <span className="detail-comment-author">
              {maskMobileNumber(0 + item.userKey.toString())}
            </span>
          </div>
          {desktop && (
            <div className="detail-comment-reactions">
              <Button
                isIconOnly
                variant="light"
                disableRipple
                className="detail-comment-reaction"
                aria-label="نپسندیدن دیدگاه"
                onPress={() => {
                  setState(2);
                  setTimeout(
                    () =>
                      handleLike({
                        action: 2,
                        target: item._id,
                      }),
                    100
                  );
                }}
              >
                <img
                  src={
                    state === 2
                      ? "/images/thumbs-down.svg"
                      : "/images/thumbs-down-empty.svg"
                  }
                  width={30}
                  height={30}
                  alt=""
                />
              </Button>
              <Button
                isIconOnly
                variant="light"
                disableRipple
                className="detail-comment-reaction"
                aria-label="پسندیدن دیدگاه"
                onPress={() => {
                  setState(1);
                  setTimeout(
                    () =>
                      handleLike({
                        action: 1,
                        target: item._id,
                      }),
                    100
                  );
                }}
              >
                <img
                  src={
                    state === 1
                      ? "/images/thumbs-up.svg"
                      : "/images/thumbs-up-empty.svg"
                  }
                  width={30}
                  height={30}
                  alt=""
                />
              </Button>
            </div>
          )}
        </div>
        {!desktop && <div className="detail-comment-divider" />}
        <div className="detail-comment-body">
          <span>{item.body}</span>
        </div>
      </div>
    );
  };

  const handleSubmitComment = async () => {
    if (!body || body?.length < 3) {
      setValidationError("متن دیدگاه نمی تواند کمتر از ۳ حرف باشد");
      return;
    }
    setValidationError("");
    setSubmitting(true);
    try {
      const response =
        await CommentApi.prototype.commentControllerCreateComment(
          {
            body,
            contentKey,
          },
          {
            headers: {
              Authorization: `Bearer ${Cookies.get("userAccessToken")}`,
            },
          }
        );
      if (response.status === 201) {
        setBody("");
        enqueueSnackbar(
          "دیدگاه با موفقیت ثبت شد و پس از تائید در سایت قابل مشاهده خواهد بود"
        );
        reloadComments();
      } else {
        enqueueSnackbar(response?.message || "ثبت دیدگاه با خطا مواجه شد");
      }
    } catch (error) {
      enqueueSnackbar(
        error.response?.message || error.message || "ثبت دیدگاه با خطا مواجه شد"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="detail-comments">
      <div className="detail-comment-composer-wrap">
        <h3 className="detail-section-title">دیدگاه‌ها</h3>
        <div className="detail-comment-composer">
          {desktop && (
            <div className="detail-comment-avatar detail-comment-avatar-primary">
              <CommentPrimaryUserIcon />
            </div>
          )}
          <div className="detail-comment-input-wrap">
            <Textarea
              className="detail-comment-input dark"
              classNames={{
                input:
                  !profile.isLogin &&
                  "text-sm text-center md:text-start md:text-md",
                innerWrapper: classNames(
                  !profile.isLogin &&
                    "flex flex-col md:flex-row justify-center items-center sm:justify-start sm:items-start",
                  "items-center"
                ),
              }}
              value={body}
              aria-label="متن دیدگاه"
              placeholder={
                profile.isLogin ? "دیدگاه خود را بنویسید…" : undefined
              }
              onChange={(e) => {
                setBody(e.target.value);
                if (validationError) {
                  setValidationError("");
                }
              }}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  !e.shiftKey &&
                  profile.isLogin &&
                  !submitting
                ) {
                  e.preventDefault();
                  handleSubmitComment();
                }
              }}
              isInvalid={!!validationError}
              errorMessage={validationError}
              size="md"
              minRows={1}
              isReadOnly={!profile.isLogin}
              isDisabled={isLoading}
              endContent={
                profile.isLogin ? (
                  <Button
                    isDisabled={isLoading}
                    isLoading={submitting}
                    isIconOnly
                    variant="light"
                    className="detail-comment-send"
                    aria-label="ارسال دیدگاه"
                    onPress={handleSubmitComment}
                  >
                    <SendMessageLeft />
                  </Button>
                ) : (
                  <Chip
                    className={classNames(
                      "detail-comment-login mb-4 sm:mb-0",
                      !profile.isLogin && "mt-2 md:mt-0"
                    )}
                  >
                    <Link
                      href={
                        ROUTES.auth.login +
                        (typeof window !== "undefined"
                          ? `?ref=${encodeURI(window.location.pathname)}`
                          : "")
                      }
                    >
                      برای ارسال نظر وارد حساب کاربری شوید
                    </Link>
                  </Chip>
                )
              }
            />
          </div>
        </div>
      </div>
      <div className="detail-comment-list" aria-live="polite">
        {isLoading
          ? Array.from({ length: 5 })?.map((_, index) => (
              <Skeleton
                key={"shimmer_" + index}
                className="detail-comment-skeleton"
              >
                <div className="detail-comment-card">
                  <div className="detail-comment-header">
                    <div className="detail-comment-author-wrap">
                      {desktop && (
                        <div className="detail-comment-avatar detail-comment-avatar-small">
                          <CommentSecondaryUserIcon />
                        </div>
                      )}
                      <span className="detail-comment-author">XXXXX</span>
                    </div>
                    {desktop && (
                      <div className="detail-comment-reactions">
                        <div className="ml-4 cursor-pointer">
                          <ThumbsDown />
                        </div>
                        <div className="cursor-pointer">
                          <ThumbsUp />
                        </div>
                      </div>
                    )}
                  </div>
                  {!desktop && <div className="detail-comment-divider" />}
                  <div className="detail-comment-body">
                    <span>XXXXXX</span>
                  </div>
                </div>
              </Skeleton>
            ))
          : items?.length > 0 &&
            items?.map((item) => <CommentItem key={item._id} item={item} />)}
      </div>
      {hasMore && !isLoading && (
        <div className="detail-comments-more">
          <Button
            className="detail-comments-more-button"
            disableRipple
            endContent={<BottomArrow />}
            onPress={onLoadMore}
          >
            بیشتر
          </Button>
        </div>
      )}
    </div>
  );
};

export default Comments;
